'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { Plus, X, GripVertical } from 'lucide-react';
import { Button } from '@/shared/ui/shadcn/button';
import { Input } from '@/shared/ui/shadcn/input';
import { Card } from '@/shared/ui/shadcn/card';
import { ColorPickerDialog } from '@/shared/ui/ColorPickerDialog';
import { cn } from '@/shared/lib/cn';

interface CustomColorListProps {
  colors: string[];
  onColorsChange: (colors: string[]) => void;
  maxColors?: number;
  variant?: 'default' | 'compact';
}

export function CustomColorList({
  colors,
  onColorsChange,
  maxColors = 5,
  variant = 'default',
}: CustomColorListProps) {
  const t = useTranslations('render.settings.colors');
  const isCompact = variant === 'compact';

  const [colorPickerModal, setColorPickerModal] = useState<{
    isOpen: boolean;
    index: number | null;
  }>({ isOpen: false, index: null });

  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [, setTouchStartY] = useState<number | null>(null);
  const [, setTouchCurrentY] = useState<number | null>(null);
  const [touchDraggedIndex, setTouchDraggedIndex] = useState<number | null>(
    null
  );
  const isDraggingRef = useRef(false);

  const handleColorChange = useCallback(
    (index: number, color: string) => {
      const newColors = [...colors];
      newColors[index] = color;
      onColorsChange(newColors);
    },
    [colors, onColorsChange]
  );

  const handleAddColor = useCallback(() => {
    if (colors.length < maxColors) {
      onColorsChange([...colors, '#000000']);
    }
  }, [colors, maxColors, onColorsChange]);

  const handleRemoveColor = useCallback(
    (index: number) => {
      if (colors.length > 1) {
        onColorsChange(colors.filter((_, i) => i !== index));
      }
    },
    [colors, onColorsChange]
  );

  useEffect(() => {
    const preventScroll = (e: TouchEvent) => {
      if (isDraggingRef.current) {
        e.preventDefault();
      }
    };

    if (touchDraggedIndex !== null) {
      isDraggingRef.current = true;
      document.addEventListener('touchmove', preventScroll, { passive: false });
    } else {
      isDraggingRef.current = false;
    }

    return () => {
      document.removeEventListener('touchmove', preventScroll);
    };
  }, [touchDraggedIndex]);

  const handleTouchStart = (e: React.TouchEvent, index: number) => {
    if (colors.length <= 1) return;
    const touch = e.touches[0];
    if (!touch) return;
    setTouchStartY(touch.clientY);
    setTouchDraggedIndex(index);
    isDraggingRef.current = true;
    if (navigator.vibrate) {
      navigator.vibrate(10);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchDraggedIndex === null) return;
    const touch = e.touches[0];
    if (!touch) return;
    setTouchCurrentY(touch.clientY);

    const element = document.elementFromPoint(touch.clientX, touch.clientY);
    if (element) {
      const colorItem = element.closest('[data-color-index]');
      if (colorItem) {
        const targetIndex = parseInt(
          colorItem.getAttribute('data-color-index') || '0'
        );
        if (targetIndex !== touchDraggedIndex) {
          const newColors = [...colors];
          const draggedColor = newColors[touchDraggedIndex];
          if (!draggedColor) return;

          newColors.splice(touchDraggedIndex, 1);
          newColors.splice(targetIndex, 0, draggedColor);
          onColorsChange(newColors);
          setTouchDraggedIndex(targetIndex);
        }
      }
    }
  };

  const handleTouchEnd = () => {
    const allColorItems = document.querySelectorAll('[data-color-index]');
    allColorItems.forEach((item) => {
      (item as HTMLElement).style.opacity = '1';
    });

    isDraggingRef.current = false;
    setTouchDraggedIndex(null);
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newColors = [...colors];
    const draggedColor = newColors[draggedIndex];
    if (!draggedColor) return;

    newColors.splice(draggedIndex, 1);
    newColors.splice(index, 0, draggedColor);

    onColorsChange(newColors);
    setDraggedIndex(null);
  };

  const renderColorItem = (color: string, index: number) => {
    const isDragging = draggedIndex === index || touchDraggedIndex === index;

    return (
      <div
        key={index}
        data-color-index={index}
        className={cn(
          'flex items-center gap-2 rounded-lg p-2 transition-all',
          isDragging && 'opacity-50',
          'hover:bg-muted/50',
          'touch-none'
        )}
        draggable={colors.length > 1}
        onDragStart={(e) => handleDragStart(e, index)}
        onDragOver={handleDragOver}
        onDrop={(e) => handleDrop(e, index)}
        onDragEnd={() => setDraggedIndex(null)}
        onTouchStart={(e) => handleTouchStart(e, index)}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {colors.length > 1 && (
          <div className="cursor-move text-muted-foreground hover:text-foreground p-1 -m-1 touch-manipulation">
            <GripVertical
              className={cn('h-4 w-4', !isCompact && 'h-5 w-5 md:h-4 md:w-4')}
            />
          </div>
        )}
        <Button
          type="button"
          variant="outline"
          onClick={() => setColorPickerModal({ isOpen: true, index })}
          className={cn(
            'p-0 rounded-lg border-2 shrink-0',
            isCompact ? 'h-10 w-10' : 'h-12 w-12'
          )}
          style={{ backgroundColor: color }}
        />
        <Input
          type="text"
          value={color}
          onChange={(e) => handleColorChange(index, e.target.value)}
          placeholder="#000000"
          className="flex-1"
        />
        {colors.length > 1 && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => handleRemoveColor(index)}
            className={cn('shrink-0', isCompact && 'h-8 w-8')}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    );
  };

  return (
    <>
      <Card
        className={cn(
          'border-2 border-primary',
          isCompact ? 'p-3 space-y-3' : 'p-4 md:p-6 space-y-4'
        )}
      >
        {colors.map((color, index) => renderColorItem(color, index))}
        {colors.length < maxColors && (
          <Button
            type="button"
            variant="outline"
            onClick={handleAddColor}
            className="w-full"
            size={isCompact ? 'sm' : 'default'}
          >
            <Plus className="h-4 w-4 mr-2" />
            {t('addColor')}
          </Button>
        )}
      </Card>

      <ColorPickerDialog
        open={colorPickerModal.isOpen}
        onOpenChange={(open) => {
          if (!open) {
            setColorPickerModal({ isOpen: false, index: null });
          }
        }}
        color={
          colorPickerModal.index !== null
            ? (colors[colorPickerModal.index] ?? '#000000')
            : '#000000'
        }
        onChange={(color) => {
          if (colorPickerModal.index !== null && color) {
            handleColorChange(colorPickerModal.index, color);
          }
          setColorPickerModal({ isOpen: false, index: null });
        }}
        title={
          colorPickerModal.index !== null
            ? t('colorPickerTitle', { index: colorPickerModal.index + 1 })
            : t('colorPickerTitle', { index: 1 })
        }
      />
    </>
  );
}
